<script lang="ts">import { Button } from "@infinitro/button";
import { cfgBrand, cfgBrandTitle, cfgSupportEmail, cfgToThemeCdnPath, cfgToThemeContentPath, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { closeThisModal, isMobile, locale, ModalDialog, modalStack } from "@infinitro/global";
import { Modal } from "@infinitro/modal";
import MarkdownIt from "markdown-it";
import { derived } from "svelte/store";
const thisModal = ModalDialog.Geoblock;
const closeModal = () => closeThisModal(thisModal);
const modalIndex = derived(modalStack, ($modalStack) => $modalStack.findIndex((e) => e.modal === thisModal));
const payload = derived(modalStack, ($modalStack) => $modalStack.find((e) => e.modal === thisModal)?.data);
const zOverlay = derived(modalIndex, ($modalIndex) => $modalIndex >= 0 ? 9999 + $modalIndex * 2 : 9999);
const label = derived(payload, ($payload) => $payload?.label);
const hardBan = derived(payload, ($payload) => $payload?.hard === true);
const BRAND_TITLE = cfgBrandTitle().toUpperCase();
const BRAND = cfgBrand();
const AlertImage = cfgToThemeCdnPath(`/geoblock/alert.png`);
const supportEmail = cfgSupportEmail();
const supportEmailStyles = "color: var(--color-primary-500);";
const CONTENT_BASE_PATH = "/geoblock/geoblock";
const md = new MarkdownIt({
	html: true,
	linkify: true,
	breaks: true
});
export let onClose = undefined;
export let onSupport = undefined;
const close = () => {
	if ($hardBan) return;
	if (onClose && typeof onClose === "function") {
		onClose();
	}
	closeModal();
};
const extractISO2 = (s) => {
	const m = s?.match(/\((\w{2})\)/);
	if (m) return m[1].toUpperCase();
	const tail = s?.trim().match(/\b[A-Z]{2}\b$/)?.[0];
	return tail ? tail.toUpperCase() : null;
};
const extractISO2FromStart = (s) => {
	const m = s?.match(/^\s*([A-Za-z]{2})\s*-\s*/);
	return m ? m[1].toUpperCase() : null;
};
const stripIsoFromLabel = (s) => s?.replace(/\s*\([A-Z]{2}\)\s*$/, "") ?? s;
$: isoFromStart = extractISO2FromStart($label);
$: countryCode = isoFromStart ?? extractISO2($label);
$: sanitizedLabel = isoFromStart ? $label.replace(/^\s*[A-Za-z]{2}\s*-\s*/, "") : stripIsoFromLabel($label);
$: flagBaseName = countryCode ?? null;
let flagExt = "png";
let showFlag = true;
const onFlagError = (e) => {
	const img = e.currentTarget;
	if (img) img.onerror = null;
	showFlag = false;
};
$: flagSrc = showFlag && flagBaseName ? cfgToThemeCdnPath(`/flags/${flagBaseName}.${flagExt}`) : null;
$: useMarkdownContent = $stateUi.geoblock?.contentSource === "markdown";
let contentPaths = [];
let htmlContent = "";
function replaceMarkdownVars(markdown) {
	return markdown.replace(/%(domain|brand)%/g, BRAND_TITLE).replace(/%(country)%/g, sanitizedLabel ?? "").replace(/%(support_email)%/g, supportEmail);
}
async function fetchContent() {
	for (const path of contentPaths) {
		try {
			const response = await fetch(path);
			if (!response.ok) continue;
			const markdown = await response.text();
			htmlContent = md.render(replaceMarkdownVars(markdown));
			return;
		} catch (error) {
			console.error("Failed to fetch geoblock markdown:", error);
		}
	}
	htmlContent = "";
}
$: {
	if (useMarkdownContent) {
		contentPaths = [cfgToThemeContentPath(`${CONTENT_BASE_PATH}.${$locale}.md`), cfgToThemeContentPath(`${CONTENT_BASE_PATH}.en.md`)];
		fetchContent();
	} else {
		htmlContent = "";
	}
}
</script>

<Modal
	title={null}
	closeModal={!$hardBan ? close : () => {}}
	width="100%"
	height="85vh"
	maxWidth="64rem"
	maxHeight="85vh"
	maxHeightContent="calc(85vh - 95px)"
	padding=""
	closeByBackdrop={true}
	showCloseButton={!$hardBan}
	alwaysCentered={false}
	notFullScreenMobile={false}
	zIndexOverlay={$zOverlay}
	zIndexUnderlay={$zOverlay - 1}
>
	<div class="geoblock">
		<div class="content">
			<img
				src={AlertImage}
				alt={$t(Verbiage.geoblock_alert_alt, 'alts')}
				class="geoblock-alert"
				loading="lazy"
			/>

			<p class="country">
				{#if flagSrc}
					<img
						src={flagSrc}
						alt={$t(Verbiage.icon_alt, 'alts')}
						class="flag-img"
						loading="lazy"
						on:error={onFlagError}
					/>
				{/if}
				{sanitizedLabel}
			</p>

			{#if htmlContent}
				<div class="geoblock-markdown">{@html htmlContent}</div>
			{:else}
				<p class="text main-text">
					{@html $t(Verbiage.sorry_text, 'global', { BRAND: BRAND_TITLE })}
				</p>

				<p class="subtext">
					{#if $isMobile}
						{@html $t(Verbiage.gaming_license_norms_text_mobile, 'global', {
							BRAND: BRAND,
							SUPPORT_EMAIL: supportEmail,
							SUPPORT_EMAIL_STYLES: supportEmailStyles
						})}
					{:else}
						{@html $t(Verbiage.gaming_license_norms_text, 'global', {
							BRAND: BRAND,
							SUPPORT_EMAIL: supportEmail,
							SUPPORT_EMAIL_STYLES: supportEmailStyles
						})}
					{/if}
				</p>
			{/if}

			{#if onSupport !== undefined}
				<Button
					text={$t(Verbiage.contact_support)}
					variant="tertiary"
					onClick={() => onSupport()}
				/>
			{/if}
		</div>
	</div>
</Modal>

<style>
	.geoblock {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	.illustration {
		position: absolute;
		inset: 0px;
		display: none;
		height: 100%;
		width: 100%;
}

	@media (min-width: 768px) {

		.illustration {
				display: block;
		}
}

	.illustration {
		z-index: 0;
		-o-object-fit: cover;
		   object-fit: cover;
	}

	.content {
		position: relative;
		z-index: 1;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	.content > :not([hidden]) ~ :not([hidden]) {
		--tw-space-y-reverse: 0;
		margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse)));
		margin-bottom: calc(0.75rem * var(--tw-space-y-reverse));
}

	.content {
		text-align: center;
}

	.country {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 600;
		color: var(--color-error-500);
}

	.flag-img {
		display: inline-block;
		vertical-align: middle;
		width: 28px;
		height: 20px;
		-o-object-fit: cover;
		   object-fit: cover;
		border-radius: 4px;
}

	.main-text {
		font-size: 1.25rem;
		line-height: 1.75rem;
		font-weight: 700;
}

	.subtext {
		padding-bottom: 1.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.subtext {
				max-width: 50%;
		}
}

	.subtext a {
		text-decoration-line: none;
		color: #1bffa6;
}

	.geoblock-markdown {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	:global(.geoblock-markdown h1) {
		margin: 0px;
		margin-top: 0.25rem;
		margin-bottom: 1rem;
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 600;
		line-height: 1.25;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 768px) {

		:global(.geoblock-markdown h1) {
				font-size: 1.875rem;
				line-height: 2.25rem;
		}
}

	:global(.geoblock-markdown h2) {
		margin: 0px;
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-300);
}

	@media (min-width: 768px) {

		:global(.geoblock-markdown h2) {
				font-size: 1rem;
				line-height: 1.5rem;
		}
}

	:global(.geoblock-markdown p) {
		margin-bottom: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		line-height: 1.625;
		color: var(--color-text-100);
}

	@media (min-width: 1024px) {

		:global(.geoblock-markdown p) {
				max-width: 50%;
		}
}

	:global(.geoblock-markdown a) {
		color: var(--color-highlight-5);
}

	:global(.geoblock-markdown hr) {
		margin: 0px;
		height: 0px;
		border-width: 0px;
}

	.links-container {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 2rem;
}

	.link-item {
		display: flex;
		cursor: pointer;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	.link-icon {
		height: 1.75rem;
		width: 1.75rem;
}

	.geoblock-alert {
		height: auto;
		width: 280px;
}
</style>
